import { AxiosInstance } from "axios";
import useSWR from "swr";

import { errorFormatter } from './serviceUtils/responseFormatter';
import { Error } from './types/CommonTypes';
import { Comment, CommentResponse } from './types/CommentTypes';
import { fetcherWithToken } from "./ApiClient";

export const useComments = (submissionURL: string, pageNumber: number, token: string) => {
  const shouldFetch = !!submissionURL && !!token;

  const queryParams = new URLSearchParams({
    object: submissionURL,
    sort: 'created',
    'page[number]': pageNumber.toString(),
  });

  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? [`${process.env.REACT_APP_DOMAIN}/api/comments/?${queryParams.toString()}`, token] : null,
    ([url, token]) => fetcherWithToken(url, token)
  );

  return {
    data: data as CommentResponse,
    isLoading: isLoading,
    isError: error,
    mutate: mutate,
  };
};

// Comment functions integrated into TaskService - no separate comment service needed
export interface CommentProps {
  likeComment: (commentId: string) => Promise<{ success: boolean; error?: Error }>;
  unlikeComment: (commentId: string) => Promise<{ success: boolean; error?: Error }>;
  submitComment: (data: {
    object: string;
    message: string;
    parentCommentId?: string
  }) => Promise<{ success: boolean; data?: Comment; error?: Error }>;
}

// This will be integrated into TaskService, not used separately
function createCommentService(axiosClient: AxiosInstance): CommentProps {
  return {
    likeComment: async (commentId: string): Promise<{ success: boolean; error?: Error }> => {
      return axiosClient.post(`/api/comments/${commentId}/like/`).then(
        (res) => ({ success: true }),
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { success: false, error: formattedError };
        }
      );
    },

    unlikeComment: async (commentId: string): Promise<{ success: boolean; error?: Error }> => {
      return axiosClient.delete(`/api/comments/${commentId}/like/`).then(
        (res) => ({ success: true }),
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { success: false, error: formattedError };
        }
      );
    },

    submitComment: async ({
      object,
      message,
      parentCommentId
    }): Promise<{ success: boolean; data?: Comment; error?: Error }> => {
      const payload: any = {
        object,
        message,
      };

      // Add parent comment reference if replying to a comment
      if (parentCommentId) {
        payload.parent = `${process.env.REACT_APP_DOMAIN}/api/comments/${parentCommentId}/`;
      }

      return axiosClient.post('/api/comments/', payload).then(
        (res) => ({ success: true, data: res.data }),
        (err) => {
          const { errors }: { errors: Error[] } = err?.response?.data || {};
          const formattedError = errorFormatter(errors);
          return { success: false, error: formattedError };
        }
      );
    },
  };
}

export default createCommentService;